import React, {FC, useMemo} from 'react';
import {
  Box,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Typography,
} from '@mui/material';
import {
  DndContext,
  DragEndEvent,
  DraggableAttributes,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import {CSS} from '@dnd-kit/utilities';
//import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import {t} from 'i18next';
import ArrTooltip from '../../../../../common/arr-tooltip/ArrTooltip';
import {KeyValue} from '../../../../../common/arr-transfer-list/ArrTransferList';
import './EditPanelReorderList.scss';
import {SyntheticListenerMap} from '@dnd-kit/core/dist/hooks/utilities';
import {ReactComponent as OrderIcon} from '../../../../../../assets/images/order_icon.svg';
import {ReactComponent as DeleteIcon} from '../../../../../../assets/images/delete-icon.svg';

type Props = {
  title?: string;
  items: KeyValue[];
  onReorder: (newOrder: KeyValue[]) => void;
  onRemove?: (field: KeyValue) => void;
};

const SortableRow: FC<{
  item: KeyValue;
  children: (handleProps: {
    attributes: DraggableAttributes;
    listeners: SyntheticListenerMap;
  }) => React.ReactNode;
}> = ({item, children}) => {
  const {attributes, listeners, setNodeRef, transform, transition} =
    useSortable({
      id: item.id,
    });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  } as React.CSSProperties;
  const handleAttributes = (attributes as DraggableAttributes) ?? {};
  const handleListeners = (listeners as SyntheticListenerMap) ?? {};
  return (
    <ListItem ref={setNodeRef} style={style} className="list-item-row">
      {children({attributes: handleAttributes, listeners: handleListeners})}
    </ListItem>
  );
};

const EditPanelReorderList: FC<Props> = ({
  title = 'Selected',
  items,
  onReorder,
  onRemove,
}) => {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {distance: 5},
    })
  );

  const ids = useMemo(() => items.map((i) => i.id), [items]);

  const handleDragEnd = (event: DragEndEvent) => {
    const {active, over} = event;
    if (!over || active.id === over.id) return;
    const oldIndex = ids.indexOf(active.id as number);
    const newIndex = ids.indexOf(over.id as number);
    if (oldIndex === -1 || newIndex === -1) return;
    const newItems = arrayMove(items, oldIndex, newIndex);
    onReorder(newItems);
  };

  return (
    <Box className="reorder-container-list">
      <div
        className="title-container"
        data-testid={`edit-panel-reorder-list-maximum-columns`}
      >
        <Box className="title-content">
          <Typography
            className="typo-text subhead4_size text box-sub-heading"
            data-testid={`edit-panel-reorder-list-title`}
          >
            {title}
          </Typography>
          <Typography
            className="validation-message"
            data-testid={`edit-panel-reorder-list-maximum-columns-text`}
          >
            {t('docView.editpanel.maximumColumns')}
          </Typography>
        </Box>
      </div>
      <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
        <SortableContext items={ids} strategy={verticalListSortingStrategy}>
          <List className="list-item">
            {items.map((field, index) => (
              <SortableRow key={field.id} item={field}>
                {({attributes, listeners}) => (
                  <ListItemText
                    primary={
                      <div
                        className="reorder-row-content"
                        data-testid={`edit-panel-reorder-list-div-${index}`}
                      >
                        <span
                          {...attributes}
                          {...listeners}
                          className="drag-handle-icon"
                          data-testid={`edit-panel-reorder-list-order-icon-${index}`}
                        >
                          <OrderIcon
                            data-testid={`edit-panel-reorder-list-order-icon-svg-${index}`}
                          />
                        </span>
                        <div
                          className="transfer-label-container reorder-flex"
                          data-testid={`edit-panel-reorder-list-div-tooltip-${index}`}
                        >
                          <ArrTooltip content={field.label}>
                            <Typography
                              className="typo-text body3_size box-sub-heading"
                              noWrap
                              data-testid={`edit-panel-reorder-list-typography-label-${index}`}
                            >
                              {field.label}
                            </Typography>
                          </ArrTooltip>
                        </div>

                        <IconButton
                          size="small"
                          aria-label="remove"
                          onClick={() => onRemove?.(field)}
                          data-testid={`${field.id}-edit-panel-reorder-list-remove`}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </div>
                    }
                  />
                )}
              </SortableRow>
            ))}
          </List>
        </SortableContext>
      </DndContext>
    </Box>
  );
};

export default EditPanelReorderList;
